import { MFA_DIALOG_PARAM } from "@/app/components/mfa-verification-dialog/mfa-verification-dialog.config"
import { verifyContacts } from "@/app/components/post-login-dialog/post-login-dialog.config"
import { usePostLoginRedirectUrl } from "@/hooks/use-post-login-redirect-url"
import { shouldShowPreRegistrationSteps } from "@/lib/affiliation"
import { postLoginRedirect } from "@/lib/postLoginRedirect"
import { useCommunityProfileStatus } from "@/services/get-community-info-user-me"
import { useInfoLoginMutation } from "@/services/get-info-login"
import { TConfirmLoginResponse } from "@/services/post-login-confirm"
import {
  LoginBlockedType,
  MultiAction,
  TSsoLoginResponse,
} from "@/services/post-login-sso"
import { isOk } from "@pacote/result"
import Cookies from "js-cookie"
import { useRouter } from "next/navigation"
import { useCustomSearchParams } from "./use-search-params"

type ComputePostLoginActionParams = {
  postLoginActionType: MultiAction["type"]
  args: {
    response:
      | TSsoLoginResponse
      | (TConfirmLoginResponse & {
          redirect?: string
        })
    loginBlockedType?: LoginBlockedType
    showListModals?: boolean
    setFormError?: (error: string) => void
    redirectUrl?: URL
    isTrustedDevice?: boolean
  }
}

export function usePostLogin() {
  const [searchParams, setSearchParams] = useCustomSearchParams()
  const router = useRouter()
  const { trigger: makeInfoLogin } = useInfoLoginMutation()
  const communityProfileStatusSWR = useCommunityProfileStatus()
  const getPostLoginRedirectUrl = usePostLoginRedirectUrl()

  const handleUrlRedirect = async ({
    response,
    redirectUrl,
    isTrustedDevice,
  }: {
    response: ComputePostLoginActionParams["args"]["response"]
    redirectUrl?: URL
    isTrustedDevice?: boolean
  }) => {
    if (redirectUrl) {
      postLoginRedirect(redirectUrl)
      return
    }
    // Await for the response from the backend to determine the community profile status
    // The Community fetcher will only execute when necessary
    const communityProfileStatus = await communityProfileStatusSWR.mutate()
    const url = new URL(
      getPostLoginRedirectUrl(response, communityProfileStatus),
      window.location.origin,
    )
    // Append trusted_device flag if available
    if (isTrustedDevice != null) {
      url.searchParams.set("trusted_device", isTrustedDevice ? "true" : "false")
    }
    postLoginRedirect(url)
  }

  const handleAccountBlocked = ({
    loginBlockedType,
    setFormError = () => {},
  }: {
    loginBlockedType?: LoginBlockedType
    setFormError?: (error: string) => void
  }) => {
    if (loginBlockedType === "SUSPEND") setFormError(`Errors.invalidData`)
    if (
      loginBlockedType === "DOC" &&
      ["ES", "FR"].includes(window.APP_COUNTRY)
    ) {
      setSearchParams((prevParams) => {
        prevParams.set("foreignAccountIncomplete", "open")
        return prevParams
      })
      return
    }
    setSearchParams((prevParams) => {
      prevParams.set("accountBlocked", "open")
      return prevParams
    })
  }

  const triggerPostLoginAction = async ({
    postLoginActionType,
    args: {
      response,
      loginBlockedType,
      showListModals,
      setFormError = () => {},
      redirectUrl,
      isTrustedDevice,
    },
  }: ComputePostLoginActionParams) => {
    // PRIORITY 1
    switch (postLoginActionType) {
      case "VERIFY_CONTACTS":
        setSearchParams((prevParams) => {
          prevParams.delete(MFA_DIALOG_PARAM)
          prevParams.set(verifyContacts, "open")
          return prevParams
        })
        return
      case "MUST_ACCEPT_NEW_CONTRACT_FOR_PLATFORM_MIGRATION":
        router.push(`/migration?${searchParams.toString()}`)
        return
      case "MUST_ACCEPT_NEW_CONTRACT_AND_SELF_LIMITATIONS":
        removeJwtCookie() // Remove JWT to force re-login until the flow is complete
        router.push(`/play-safe-new-contract?${searchParams.toString()}`)
        return
      case "MUST_ACCEPT_SELF_LIMITATIONS":
        removeJwtCookie() // Remove JWT to force re-login until the flow is complete
        router.push(`/play-safe-new-user?${searchParams.toString()}`)
        return
    }

    // PRIORITY 2
    if ("jwt" in response && response.jwt) {
      const infoLoginResponse = await makeInfoLogin()
      if (
        isOk(infoLoginResponse) &&
        shouldShowPreRegistrationSteps(infoLoginResponse.value)
      ) {
        router.push(`/crea-password?${searchParams.toString()}`)
        return
      }
    }

    // PRIORITY 3
    switch (postLoginActionType) {
      case "MFA_REQUIRED":
        setSearchParams((prevParams) => {
          prevParams.set(MFA_DIALOG_PARAM, "open")
          return prevParams
        })
        return
      case "URL_REDIRECT":
        await handleUrlRedirect({ response, redirectUrl, isTrustedDevice })
        return
      case "ACCOUNT_BLOCKED":
        handleAccountBlocked({ loginBlockedType, setFormError })
        return
      case "QUESTIONARIO":
        router.push("/Questionario?" + searchParams.toString())
        return
      case "PASSWORD_EXPIRING":
        setSearchParams((prevParams) => {
          prevParams.delete(MFA_DIALOG_PARAM)
          prevParams.set("passwordExpiring", "open")
          return prevParams
        })
        return
      default:
        if (showListModals) {
          setSearchParams((prevParams) => {
            prevParams.delete(MFA_DIALOG_PARAM)
            prevParams.set("listModal", "open")
            return prevParams
          })
        } else {
          console.error(`Unhandled post-login action: ${postLoginActionType}`)
        }
    }
  }
  return { triggerPostLoginAction }
}

function removeJwtCookie() {
  Cookies.remove("JWT_ar", {
    path: "/",
    domain: window.location.host.split(".").slice(-2).join("."),
  })
}
